/**
 * Job Card Component
 * Requirements: 6.1, 6.2, 6.3, 6.4, 7.1-7.8, 10.1
 */
import { useState, useEffect, useRef, memo } from 'react';
import { api } from '../services/api';
import { pushJobToHelper, pushAnswersToHelper } from '../services/helperBridge';
import type { UserJob, JobPhase, SubcomponentType, GenerationState, GenerationType, UserPreferences } from '../types';
import { VALID_PHASES, SUBCOMPONENTS, GENERATION_TYPE_RESTRICTIONS } from '../types';
import { PHASE_COLORS, SUBCOMPONENT_LABELS, GENERATION_TYPE_LABELS } from '../constants';
import { ContextMenu, MenuItem } from './ContextMenu';
import {
  MapPin,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Loader2,
  Zap,
  SkipForward,
  Trash2,
  Pencil,
  Download,
  FileText,
  Eye,
  Brain,
  Settings,
  XCircle,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Sparkles,
  Lock,
  Send,
  PhoneForwarded,
  Search,
  PartyPopper,
} from 'lucide-react';

// Social links for the "Congrats" action (Accepted phase)
const SOCIAL_LINKS = [
  {
    label: 'LinkedIn',
    url: 'https://www.linkedin.com/company/skillsnap-me',
    shareUrl: 'https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fskillsnap.me',
  },
];

interface JobCardProps {
  job: UserJob;
  /** WARNING: onRefresh() causes full page reload (sets loading=true, unmounts component tree).
   *  Only use for destructive operations (delete, or as a fallback on error). For mutations
   *  (generate, type toggle, phase change), use onJobUpdate / onOptimisticPhaseChange. */
  onRefresh: () => void;
  onEditSubcomponent: (jobid: string, component: SubcomponentType) => void;
  onEditJob?: (jobid: string) => void;
  onGenerateResume?: (jobid: string) => void;
  onJobUpdate?: (updatedJob: UserJob) => void;
  /** Optimistic phase change — updates both the card's jobphase and the
   *  Dashboard-level phaseCounts in one shot, with no API round-trip. Same
   *  handler kanban drag-and-drop uses. Caller should call onRefresh only if
   *  the API call subsequently fails. */
  onOptimisticPhaseChange?: (jobid: string, newPhase: JobPhase) => void;
  selectedResume?: string | null;
  autoDownloadFiles?: boolean;
  userPreferences?: UserPreferences;
  onUpgradeRequest?: (reason: 'exhausted' | 'enable_job' | 'regen_section') => void;
  onFreeGenConsumed?: () => void;
}


// All allowed generation types (fallback if no restriction defined)
const ALL_GENERATION_TYPES: GenerationType[] = ['generate', 'embedding', 'manual', 'omit'];

const JobCardComponent = ({ job, onRefresh, onEditSubcomponent, onEditJob, onGenerateResume, onJobUpdate, onOptimisticPhaseChange, selectedResume, autoDownloadFiles = true, userPreferences, onUpgradeRequest, onFreeGenConsumed }: JobCardProps) => {
  const userStatus = userPreferences?.status;
  const isTrialOrHib = userStatus === 'trial' || userStatus === 'hibernating';
  const isJobDisabled = isTrialOrHib && job.is_enabled === false;
  const canRegenerateSection = userPreferences ? userPreferences.can_regenerate_section !== false : true;
  const [showCardOverlay, setShowCardOverlay] = useState(false);
  const [showMoveToMenu, setShowMoveToMenu] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingComponents, setGeneratingComponents] = useState<Set<SubcomponentType>>(new Set());
  const [generationStartTimes, setGenerationStartTimes] = useState<Map<SubcomponentType, number>>(new Map());
  // Track whether user has explicitly toggled the accordion, and persist the value
  const userToggledAccordion = useRef(false);
  const accordionValueRef = useRef(false);
  const [resumeSectionsExpanded, setResumeSectionsExpanded] = useState(accordionValueRef.current);

  // Context menu state for resume sections
  const [sectionContextMenu, setSectionContextMenu] = useState<{
    position: { x: number; y: number };
    component: SubcomponentType;
  } | null>(null);

  // Context menu state for file rows
  const [fileContextMenu, setFileContextMenu] = useState<{
    position: { x: number; y: number };
    fileType: 'resume' | 'cover';
  } | null>(null);

  const handleResumeSectionsToggle = () => {
    userToggledAccordion.current = true;
    setResumeSectionsExpanded(prev => {
      const next = !prev;
      accordionValueRef.current = next;
      return next;
    });
  };

  // Workday PDF generation state
  const [generatingWorkday, setGeneratingWorkday] = useState(false);

  // Phase-change dismiss animation
  const [dismissing, setDismissing] = useState(false);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track retry counts per component to cap auto-retries
  const retryCountsRef = useRef<Map<SubcomponentType, number>>(new Map());

  // Show a toast for a given duration
  function showToast(message: string, durationMs = 5000) {
    setToastMessage(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), durationMs);
  }

  // Auto-retry for components in 'retry' state (resume still indexing)
  useEffect(() => {
    const MAX_RETRIES = 100;
    const RETRY_DELAY_MS = 4000;
    const SUBCOMPONENTS_LIST: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter', 'elevatorpitch'];
    const timers: ReturnType<typeof setTimeout>[] = [];

    SUBCOMPONENTS_LIST.forEach(comp => {
      const state = job[`state${comp}` as keyof UserJob] as GenerationState;
      if (state === 'retry') {
        const currentRetries = retryCountsRef.current.get(comp) || 0;
        if (currentRetries < MAX_RETRIES) {
          // Progress is now shown via the in-card "generating tags" label
          // over the tag row — no recurring toast.
          retryCountsRef.current.set(comp, currentRetries + 1);
          const timer = setTimeout(() => {
            handleGenerateSingle(comp);
          }, RETRY_DELAY_MS);
          timers.push(timer);
        } else {
          // Exhausted all retries — set error state so it's visible
          showToast(`Job tag generation failed for ${comp} after ${MAX_RETRIES} retries. Please contact support.`, 12000);
          console.error(`Retry exhausted for ${comp} on job ${job.jobid} after ${MAX_RETRIES} retries`);
        }
      } else if (state === 'complete' || state === 'error') {
        // Reset retry count once resolved
        retryCountsRef.current.delete(comp);
      }
      // Show error messages from backend
      if (state === 'error') {
        const msg = (job as any)[`msg${comp}`] as string | undefined;
        if (msg) showToast(msg, 8000);
      }
    });

    return () => timers.forEach(t => clearTimeout(t));
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local timeout: clear spinning icons after 5 minutes
  useEffect(() => {
    const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

    const checkTimeouts = () => {
      const now = Date.now();
      const timedOutComponents: SubcomponentType[] = [];

      generationStartTimes.forEach((startTime, component) => {
        if ((now - startTime) > TIMEOUT_MS) {
          console.warn(`Generation timeout for ${job.jobid} ${component} - clearing spinner after 5 minutes`);
          timedOutComponents.push(component);
        }
      });

      if (timedOutComponents.length > 0) {
        // Remove timed-out components from both sets
        setGeneratingComponents(prev => {
          const next = new Set(prev);
          timedOutComponents.forEach(comp => next.delete(comp));
          return next;
        });
        setGenerationStartTimes(prev => {
          const next = new Map(prev);
          timedOutComponents.forEach(comp => next.delete(comp));
          return next;
        });
      }
    };

    // Check every 10 seconds
    const interval = setInterval(checkTimeouts, 10000);

    return () => clearInterval(interval);
  }, [generationStartTimes, job.jobid]);

  const [deleting, setDeleting] = useState(false);

  async function handleDeleteJob() {
    if (!confirm('Permanently delete this job? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.deleteJob(job.jobid);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete job:', error);
      alert('Failed to delete job. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  async function handlePhaseChange(phase: JobPhase) {
    // Brief fade-out animation so the user sees the card leave
    setShowMoveToMenu(false);
    setDismissing(true);
    setTimeout(() => {
      if (onOptimisticPhaseChange) {
        onOptimisticPhaseChange(job.jobid, phase);
      }
    }, 250);
    try {
      await api.updateJobPhase(job.jobid, phase);
    } catch (error) {
      console.error('Failed to update phase:', error);
      setDismissing(false);
      alert(`Failed to move job to ${phase}. Please try again.`);
      onRefresh();
    }
  }

  async function handleGenerateAll() {
    // Guard: need a resume to generate
    const resumeid = selectedResume || job.resumeid;
    if (!resumeid) {
      alert('Please select a resume before generating. Use the resume dropdown in the header.');
      return;
    }

    setGenerating(true);
    // Only generate non-omit components that don't already have data
    // Exception: if job is in Queued phase, regenerate ALL sections (full regen)
    const isFullRegen = job.jobphase === 'Queued';
    const componentsToGenerate = SUBCOMPONENTS.filter(comp => {
      const type = job[`type${comp}` as keyof UserJob] as GenerationType;
      if (type === 'omit') return false;
      if (isFullRegen) return true; // Queued: regenerate everything
      // Skip sections that already have generated content
      const state = job[`state${comp}` as keyof UserJob] as GenerationState;
      if (state === 'complete' && hasData(comp)) return false;
      return true;
    });

    if (componentsToGenerate.length === 0) {
      // All sections already generated - trigger final files directly
      setGenerating(false);
      onGenerateResume?.(job.jobid);
      return;
    }

    setGeneratingComponents(new Set(componentsToGenerate));
    // Record start time for all components
    const now = Date.now();
    setGenerationStartTimes(new Map(componentsToGenerate.map(comp => [comp, now])));

    // Optimistic: move the card into "Generating" phase immediately and mark
    // each component as generating, so the UI moves the card without waiting
    // for the 10-20s API round-trip (ensure_job_tags can synchronously call
    // gen_tags Lambda for jobs missing tags). The server flips jobphase to
    // 'Generating' on the same fields, so this matches what's about to happen.
    // If the API call fails, onRefresh() below snaps state back to reality.
    const wasPhase = job.jobphase;
    if (job.jobphase !== 'Generating' && onOptimisticPhaseChange) {
      onOptimisticPhaseChange(job.jobid, 'Generating');
    }
    if (onJobUpdate) {
      const updates: Partial<UserJob> = { jobphase: 'Generating' };
      componentsToGenerate.forEach(comp => {
        (updates as any)[`state${comp}`] = 'generating';
      });
      onJobUpdate({ ...job, ...updates } as UserJob);
    }

    try {
      await api.generateAll(job.jobid, componentsToGenerate, resumeid);
      // Signal to parent that auto-final should start
      onGenerateResume?.(job.jobid);
      if (isTrialOrHib && job.is_enabled === false) {
        if (onJobUpdate) {
          onJobUpdate({ ...job, is_enabled: true, enabled_at: new Date().toISOString() } as UserJob);
        }
        onFreeGenConsumed?.();
      }
      // Keep the optimistic UI state for a bit to ensure polling starts
      setTimeout(() => {
        setGenerating(false);
        setGeneratingComponents(new Set());
        setGenerationStartTimes(new Map());
      }, 1000);
    } catch (error: any) {
      console.error('Failed to generate all:', error);
      setGenerating(false);
      setGeneratingComponents(new Set());
      setGenerationStartTimes(new Map());
      // Roll back the optimistic phase move: snap back by refetching from API.
      if (wasPhase !== 'Generating' && onOptimisticPhaseChange) {
        onOptimisticPhaseChange(job.jobid, wasPhase);
      }
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.error;
      if (status === 402 || errorCode === 'no_free_generations') {
        onUpgradeRequest?.('exhausted');
        return;
      }
      onRefresh();
    }
  }

  async function handleGenerateSingle(component: SubcomponentType) {
    const resumeid = selectedResume || job.resumeid;
    if (!resumeid) {
      alert('Please select a resume before generating. Use the resume dropdown in the header.');
      return;
    }

    setGeneratingComponents(prev => new Set(prev).add(component));
    setGenerationStartTimes(prev => new Map(prev).set(component, Date.now()));
    // Mark the corresponding file as stale (content changed, final file not yet regenerated)
    // Elevator pitch is a pseudo-section — not rendered in resume or coverletter
    if (component !== 'elevatorpitch') {
      const staleTarget = component === 'coverletter' ? 'cover' : 'resume';
      setStaleFiles(prev => new Set(prev).add(staleTarget));
    }
    try {
      await api.generateSingle(job.jobid, component, resumeid);
      // Optimistically mark component as generating so Dashboard polling kicks in
      if (onJobUpdate) {
        const stateKey = `state${component}` as keyof UserJob;
        onJobUpdate({ ...job, [stateKey]: 'generating' } as UserJob);
      }
      // Keep the optimistic UI state for a bit to ensure polling starts
      setTimeout(() => {
        setGeneratingComponents(prev => {
          const next = new Set(prev);
          next.delete(component);
          return next;
        });
        setGenerationStartTimes(prev => {
          const next = new Map(prev);
          next.delete(component);
          return next;
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to generate:', error);
      setGeneratingComponents(prev => {
        const next = new Set(prev);
        next.delete(component);
        return next;
      });
      setGenerationStartTimes(prev => {
        const next = new Map(prev);
        next.delete(component);
        return next;
      });
    }
  }

  async function handleSetType(component: SubcomponentType, newType: GenerationType) {
    try {
      await api.toggleGenerationType(job.jobid, component, newType);
      // Optimistic update: update the job in-place instead of triggering a full refresh
      if (onJobUpdate) {
        const typeKey = `type${component}` as keyof UserJob;
        const updatedJob = { ...job, [typeKey]: newType };
        onJobUpdate(updatedJob as UserJob);
      } else {
        // Fallback to full refresh if no onJobUpdate callback provided
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to set type:', error);
    }
  }

  // Check if component has data
  function hasData(component: SubcomponentType): boolean {
    const data = job[`data${component}` as keyof UserJob] as string | undefined;
    return !!data && data.trim().length > 0;
  }

  // Check if a final file exists (extended for txt and template)
  function hasFinalFile(type: 'resume' | 'cover', format: 'html' | 'pdf' | 'txt' | 'template'): boolean {
    if (type === 'resume') {
      if (format === 'html') return !!job.s3locresumehtml;
      if (format === 'pdf') return !!job.s3locresumepdf;
      if (format === 'txt') return !!job.s3locresumetxt;
      if (format === 'template') return !!job.s3locresumetemplate;
    } else {
      if (format === 'html') return !!job.s3loccoverletterhtml;
      if (format === 'pdf') return !!job.s3loccoverletterpdf;
      if (format === 'txt') return !!job.s3loccoverlettertxt;
    }
    return false;
  }

  // Get final file URL using custom domain (*.skillsnap.me)
  function getFinalFileUrl(type: 'resume' | 'cover', format: 'html' | 'pdf' | 'txt' | 'template'): string | null {
    let s3loc: string | undefined;
    if (type === 'resume') {
      if (format === 'html') s3loc = job.s3locresumehtml;
      else if (format === 'pdf') s3loc = job.s3locresumepdf;
      else if (format === 'txt') s3loc = job.s3locresumetxt;
      else if (format === 'template') s3loc = job.s3locresumetemplate;
    } else {
      if (format === 'html') s3loc = job.s3loccoverletterhtml;
      else if (format === 'pdf') s3loc = job.s3loccoverletterpdf;
      else if (format === 'txt') s3loc = job.s3loccoverlettertxt;
    }
    if (!s3loc) return null;

    // Strip s3://skillsnap-public-resumes/ prefix to get path
    const path = s3loc.replace(/^s3:\/\/skillsnap-public-resumes\//, '');
    // Path format: {username}/{company}/{jobtitlesafe}/{filename}
    const parts = path.split('/');
    if (parts.length >= 4) {
      const username = parts[0];
      const company = parts[1];
      const jobtitlesafe = parts[2];
      // Resume.html links to root directory (serves index.html = Resume.html)
      if (type === 'resume' && format === 'html') {
        return `https://${username}.skillsnap.me/${company}/${jobtitlesafe}`;
      }
      const filename = parts.slice(3).join('/');
      return `https://${username}.skillsnap.me/${company}/${jobtitlesafe}/${filename}`;
    }
    // Fallback: use cleaned path with CloudFront
    return `https://d2qz6zc5zvuvjc.cloudfront.net/${path}`;
  }

  // View + (optionally) download a PDF in a single CORS fetch.
  //
  // Why one fetch and not fetch+window.open(url): the resumes CloudFront
  // distribution serves *.skillsnap.me and its cache key does not vary on the
  // Origin header. A `fetch()` sends `Origin: app.skillsnap.me` (so CloudFront
  // adds Access-Control-Allow-Origin), but `window.open(url)` is a top-level
  // navigation with no Origin (so the response has no ACAO). Whichever request
  // misses the edge cache first poisons it for the other — intermittently
  // breaking the cross-origin fetch with a silent CORS error and skipping the
  // download. Fetching once and reusing the blob: URL for both the viewer tab
  // and the <a download> click eliminates the race entirely.
  function openPdf(url: string, fileType: 'resume' | 'cover') {
    if (!autoDownloadFiles) {
      window.open(url, '_blank');
      return;
    }
    // Reserve the popup synchronously so it isn't blocked after the await.
    const viewer = window.open('', '_blank');
    const company = (job.jobcompany || 'company').replace(/\s+/g, '').replace(/[,\.]/g, '');
    const candidateName = (job.dataname || 'user').replace(/\s+/g, '');
    const suffix = fileType === 'resume' ? 'resume' : 'coverletter';
    const downloadName = `${company}.${candidateName}.${suffix}.pdf`;

    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        if (viewer) viewer.location.href = blobUrl;
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Keep the blob alive long enough for the viewer tab to load it.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch(err => {
        console.error('PDF fetch failed, falling back to direct URL:', err);
        if (viewer) viewer.location.href = url;
      });
  }

  // Generate and download Workday Import PDF on-demand
  async function handleWorkdayDownload() {
    setGeneratingWorkday(true);
    setFileContextMenu(null);
    try {
      const result = await api.generateWorkdayPdf(job.jobid);
      if (result.publicUrl) {
        const company = (job.jobcompany || 'company').replace(/\s+/g, '').replace(/[,\.]/g, '');
        const candidateName = (job.dataname || 'user').replace(/\s+/g, '');
        const downloadName = `${company}.${candidateName}.workday.pdf`;
        // Fetch blob and trigger download
        const res = await fetch(result.publicUrl);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch (err) {
      console.error('Workday PDF generation failed:', err);
      showToast('Failed to generate Workday PDF');
    } finally {
      setGeneratingWorkday(false);
    }
  }

  // Check if any resume file exists
  function hasAnyResumeFile(): boolean {
    return hasFinalFile('resume', 'html') || hasFinalFile('resume', 'pdf') ||
      hasFinalFile('resume', 'txt') || hasFinalFile('resume', 'template');
  }

  // Check if any cover letter file exists
  function hasAnyCoverFile(): boolean {
    return hasFinalFile('cover', 'html') || hasFinalFile('cover', 'pdf') ||
      hasFinalFile('cover', 'txt');
  }

  // Build context menu items for a resume section row
  function buildSectionMenuItems(component: SubcomponentType): MenuItem[] {
    const currentType = (job[`type${component}` as keyof UserJob] as GenerationType) || 'embedding';
    const restrictions = GENERATION_TYPE_RESTRICTIONS[component];
    const allowedTypes: GenerationType[] = restrictions || ALL_GENERATION_TYPES;

    const regenLocked = !canRegenerateSection;
    const items: MenuItem[] = [
      {
        label: 'View / Edit Content',
        icon: <Eye className="w-4 h-4" />,
        onClick: () => onEditSubcomponent(job.jobid, component),
      },
      {
        label: regenLocked ? 'Regenerate Content (Active only)' : 'Regenerate Content',
        icon: regenLocked ? <Lock className="w-4 h-4" /> : <Zap className="w-4 h-4" />,
        onClick: regenLocked
          ? () => onUpgradeRequest?.('regen_section')
          : () => handleGenerateSingle(component),
      },
      { divider: true },
      {
        label: 'Set Generation',
        submenu: allowedTypes.map(genType => ({
          label: GENERATION_TYPE_LABELS[genType],
          active: currentType === genType,
          onClick: () => handleSetType(component, genType),
        })),
      },
    ];

    return items;
  }

  // State for tracking file regeneration — tracks each type independently
  const [regeneratingFiles, setRegeneratingFiles] = useState<Set<string>>(new Set());
  // Track when sections have been regenerated but final files haven't been re-rendered yet
  const [staleFiles, setStaleFiles] = useState<Set<string>>(new Set());

  // Feedback state (initialized from job data)
  const [feedbackResume, setFeedbackResume] = useState<'U' | 'D' | undefined>(job.feedback_resume);
  const [feedbackCover, setFeedbackCover] = useState<'U' | 'D' | undefined>(job.feedback_coverletter);

  // Keep local feedback state in sync if job data changes from polling
  useEffect(() => {
    setFeedbackResume(job.feedback_resume);
  }, [job.feedback_resume]);
  useEffect(() => {
    setFeedbackCover(job.feedback_coverletter);
  }, [job.feedback_coverletter]);

  // Error banner state for file regeneration failures
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleRegenerateFiles(fileType: 'resume' | 'cover') {
    setRegeneratingFiles(prev => new Set(prev).add(fileType));
    setFileError(null);
    // Clear stale indicator — user is regenerating the final file
    setStaleFiles(prev => { const next = new Set(prev); next.delete(fileType); return next; });
    try {
      await api.generateFinalFiles(job.jobid, fileType);
      // Polling will pick up updated files — no full page refresh needed
    } catch (error: any) {
      console.error('Failed to regenerate files:', error);
      const detail = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
      setFileError(`Regeneration failed: ${detail}`);
    } finally {
      // Keep spinner briefly so polling can start
      setTimeout(() => {
        setRegeneratingFiles(prev => {
          const next = new Set(prev);
          next.delete(fileType);
          return next;
        });
      }, 2000);
    }
  }

  // Submit feedback for a file type
  async function handleFeedback(fileType: 'resume' | 'cover', result: 'U' | 'D') {
    const topic = fileType === 'resume' ? 'resume' : 'coverletter';
    const currentFeedback = fileType === 'resume' ? feedbackResume : feedbackCover;

    // Toggle off if same button clicked again
    const newResult = currentFeedback === result ? undefined : result;

    // Optimistic update
    if (fileType === 'resume') {
      setFeedbackResume(newResult);
    } else {
      setFeedbackCover(newResult);
    }

    // Also update the job object optimistically so polling doesn't revert it
    if (onJobUpdate) {
      const feedbackField = fileType === 'resume' ? 'feedback_resume' : 'feedback_coverletter';
      onJobUpdate({ ...job, [feedbackField]: newResult } as any);
    }

    // If toggling off, we still submit the last clicked value (server will store it)
    // For a true "remove", we would need a separate endpoint, but for MVP we just toggle
    if (newResult) {
      try {
        await api.submitFeedback(job.jobid, topic, newResult);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        // Revert on error
        if (fileType === 'resume') {
          setFeedbackResume(currentFeedback);
        } else {
          setFeedbackCover(currentFeedback);
        }
      }
    }
  }

  // Build context menu items for a file row
  function buildFileMenuItems(fileType: 'resume' | 'cover'): MenuItem[] {
    const items: MenuItem[] = [];

    // Regenerate Files option at the top — only regenerates this file type
    const isRegenerating = regeneratingFiles.has(fileType);
    items.push({
      label: isRegenerating ? 'Regenerating...' : `Regenerate ${fileType === 'resume' ? 'Resume' : 'Cover Letter'}`,
      icon: <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />,
      disabled: isRegenerating,
      onClick: !isRegenerating ? () => handleRegenerateFiles(fileType) : undefined,
    });
    items.push({ divider: true });

    const hasHtml = hasFinalFile(fileType, 'html');
    const hasPdf = hasFinalFile(fileType, 'pdf');
    const hasTxt = hasFinalFile(fileType, 'txt');
    // View HTML
    items.push({
      label: 'View HTML',
      icon: <ExternalLink className="w-4 h-4" />,
      disabled: !hasHtml,
      onClick: hasHtml ? () => {
        const url = getFinalFileUrl(fileType, 'html');
        if (url) window.open(url, '_blank');
      } : undefined,
    });

    // View PDF
    items.push({
      label: 'View PDF',
      icon: <ExternalLink className="w-4 h-4" />,
      disabled: !hasPdf,
      onClick: hasPdf ? () => {
        const url = getFinalFileUrl(fileType, 'pdf');
        if (url) window.open(url, '_blank');
      } : undefined,
    });

    // View Text
    items.push({
      label: 'View Text',
      icon: <FileText className="w-4 h-4" />,
      disabled: !hasTxt,
      onClick: hasTxt ? () => {
        const url = getFinalFileUrl(fileType, 'txt');
        if (url) window.open(url, '_blank');
      } : undefined,
    });

    // Workday Import PDF (resume only)
    if (fileType === 'resume') {
      items.push({
        label: generatingWorkday ? 'Generating...' : 'Workday Import PDF',
        icon: <Download className={`w-4 h-4 ${generatingWorkday ? 'animate-spin' : ''}`} />,
        disabled: generatingWorkday || !hasPdf,
        onClick: (!generatingWorkday && hasPdf) ? () => handleWorkdayDownload() : undefined,
      });
    }

    // Feedback row
    items.push({ divider: true });

    const hasAnyFile = fileType === 'resume' ? hasAnyResumeFile() : hasAnyCoverFile();
    const currentFeedback = fileType === 'resume' ? feedbackResume : feedbackCover;

    items.push({
      customRender: (
        <div className={`flex items-center gap-2 ${hasAnyFile ? '' : 'opacity-40 pointer-events-none'}`}>
          <span className="text-xs text-gray-500 flex-1">Happy with this?</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasAnyFile) handleFeedback(fileType, 'U');
            }}
            className={`p-1 rounded transition-colors ${
              currentFeedback === 'U'
                ? 'text-green-600 bg-green-50'
                : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
            }`}
            title="Thumbs up"
            aria-label="Thumbs up"
          >
            <ThumbsUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasAnyFile) handleFeedback(fileType, 'D');
            }}
            className={`p-1 rounded transition-colors ${
              currentFeedback === 'D'
                ? 'text-red-600 bg-red-50'
                : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
            }`}
            title="Thumbs down"
            aria-label="Thumbs down"
          >
            <ThumbsDown className="w-4 h-4" />
          </button>
        </div>
      ),
    });

    return items;
  }

  // Filter out current phase and Trash for move-to options (Trash shown separately)
  const moveToPhases = VALID_PHASES.filter(p => p !== job.jobphase && p !== 'Trash');

  return (
    <div
      className={`relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-visible transition-all duration-250 ${dismissing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'} ${isJobDisabled ? 'pb-16' : ''}`}
      style={{ transitionDuration: '250ms' }}
      data-tutorial="job-card"
      onMouseEnter={() => isJobDisabled && setShowCardOverlay(true)}
      onMouseLeave={() => setShowCardOverlay(false)}
    >
      {/* Dimmed-content wrapper for disabled (trial/hibernating) cards */}
      <div className={isJobDisabled ? 'opacity-60 pointer-events-none' : ''}>
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        {/* Company Name (left) and Phase/Move To (right) */}
        <div className="flex items-start justify-between mb-2">
          {/* Company Name - Large */}
          <p className="text-lg font-bold text-gray-800 truncate flex-1 mr-3">{job.jobcompany}</p>

          {/* Phase and Move To - Right justified */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${PHASE_COLORS[job.jobphase]}`}>
              {job.jobphase}
            </span>
            <div className="relative">
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500">Move To</span>
                <button
                  onClick={() => setShowMoveToMenu(!showMoveToMenu)}
                  onBlur={() => setTimeout(() => setShowMoveToMenu(false), 150)}
                  aria-haspopup="true"
                  aria-expanded={showMoveToMenu}
                  className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center"
                >
                  Select
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </div>
              {showMoveToMenu && (
                <div role="menu" className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {moveToPhases.map((phase) => (
                    <button
                      key={phase}
                      role="menuitem"
                      onMouseDown={(e) => { e.preventDefault(); handlePhaseChange(phase); }}
                      className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100"
                    >
                      {phase}
                    </button>
                  ))}
                  <div className="border-t border-gray-200">
                    <button
                      role="menuitem"
                      onMouseDown={(e) => { e.preventDefault(); handlePhaseChange('Trash'); }}
                      className="w-full px-3 py-1.5 text-left text-xs hover:bg-red-50 text-red-600 flex items-center"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Trash
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Job Title */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {job.joburl ? (
                <a
                  href={job.joburl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-600 flex items-center"
                >
                  <span className="truncate">{job.jobtitle}</span>
                  <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
                </a>
              ) : (
                job.jobtitle
              )}
            </h3>
          </div>
          {onEditJob && (
            <button
              onClick={() => onEditJob(job.jobid)}
              className="flex items-center text-gray-500 hover:text-primary-600 ml-2"
              title="Edit Job"
              aria-label="Edit Job"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Meta info */}
        <div className="flex flex-col gap-1 mt-2 text-xs text-gray-500">
          {/* Row 1: Location, Salary */}
          {(job.joblocation || job.jobsalary) && (
            <div className="flex items-center gap-4">
              {job.joblocation && (
                <span className="flex items-center">
                  <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span>{job.joblocation}</span>
                </span>
              )}
              {job.jobsalary && (
                <span className="flex items-center">
                  <span>{job.jobsalary}</span>
                </span>
              )}
            </div>
          )}
          {/* Row 2: Age, Source */}
          <div className="flex items-center gap-4">
            <span className="flex items-center">
              <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
              {job.postingAge !== undefined ? `${job.postingAge}d ago` : job.jobposteddate}
            </span>
            <span className="text-gray-500">
              {job.joblistid ? 'LinkedIn Job Alert' : job.joburl ? 'URL Added' : 'Manually Added'}
            </span>
          </div>
        </div>

        {/* Tags - Row 3 */}
        {(() => {
          const MIN_JOB_TAGS = 50;
          const tagCount = job.jobtags?.length || 0;
          const tagsGenerating = job.jobphase === 'Generating' && tagCount < MIN_JOB_TAGS;
          if (tagsGenerating) {
            return (
              <div className="mt-1">
                <span className="inline-block px-1.5 py-0.5 bg-purple-50 text-gray-700 text-[10px] rounded animate-pulse">
                  generating tags...
                </span>
              </div>
            );
          }
          if (tagCount > 0) {
            return (
              <div className="flex flex-wrap gap-1 mt-1">
                {job.jobtags!.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded"
                  >
                    {tag}
                  </span>
                ))}
                {tagCount > 3 && (
                  <span className="text-[10px] text-gray-500">+{tagCount - 3}</span>
                )}
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Action Button and Resume Sections Accordion */}
      <div className="p-3 border-b border-gray-100">
        {/* Context-sensitive action + Skip Buttons */}
        <div className="flex gap-1 mb-2">
          {(() => {
            const phase = job.jobphase;
            const btnBase = 'flex-[3] flex items-center justify-center p-1.5 text-white text-xs rounded disabled:opacity-50';

            if (phase === 'Queued' || phase === 'Generating') {
              return (
                <button
                  data-tutorial="generate-resume"
                  onClick={handleGenerateAll}
                  disabled={generating}
                  className={`${btnBase} bg-primary-600 hover:bg-primary-700`}
                >
                  {generating ? (
                    <span role="status" aria-label="Generating">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    </span>
                  ) : (
                    <Zap className="w-4 h-4 mr-1" />
                  )}
                  Generate Resume
                </button>
              );
            }

            if (phase === 'Ready') {
              return (
                <>
                  <button
                    onClick={() => {
                      // Fire-and-forget push to the optional SkillSnap
                      // browser extension. Silent no-op if not installed.
                      pushJobToHelper(job);
                      pushAnswersToHelper(job);

                      // Reserve all popups synchronously (before async fetches)
                      // to avoid browser popup blockers.
                      const resumeUrl = getFinalFileUrl('resume', 'pdf');
                      const coverUrl = getFinalFileUrl('cover', 'pdf');
                      const jobTab = job.joburl ? window.open(job.joburl, '_blank') : null;
                      const resumeTab = resumeUrl ? window.open('', '_blank') : null;
                      const coverTab = coverUrl ? window.open('', '_blank') : null;

                      // Helper: fetch blob, load into pre-opened tab, trigger download
                      const loadPdf = (url: string, tab: Window | null, suffix: string) => {
                        const company = (job.jobcompany || 'company').replace(/\s+/g, '').replace(/[,\.]/g, '');
                        const candidateName = (job.dataname || 'user').replace(/\s+/g, '');
                        const downloadName = `${company}.${candidateName}.${suffix}.pdf`;
                        fetch(url)
                          .then(res => res.blob())
                          .then(blob => {
                            const blobUrl = URL.createObjectURL(blob);
                            if (tab) tab.location.href = blobUrl;
                            if (autoDownloadFiles) {
                              const a = document.createElement('a');
                              a.href = blobUrl;
                              a.download = downloadName;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            }
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                          })
                          .catch(() => { if (tab) tab.location.href = url; });
                      };

                      if (resumeUrl) loadPdf(resumeUrl, resumeTab, 'resume');
                      if (coverUrl) loadPdf(coverUrl, coverTab, 'coverletter');
                      void jobTab; // used above
                    }}
                    className={`${btnBase} bg-indigo-600 hover:bg-indigo-700`}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Apply
                  </button>
                  <button
                    onClick={() => handlePhaseChange('Applied')}
                    className="flex-1 flex items-center justify-center p-1.5 bg-indigo-100 text-indigo-700 text-xs rounded hover:bg-indigo-200"
                    title="Move to Applied phase"
                  >
                    Applied!
                  </button>
                </>
              );
            }

            if (phase === 'Applied') {
              return (
                <button
                  onClick={() => handlePhaseChange('Follow-Up')}
                  className={`${btnBase} bg-orange-500 hover:bg-orange-600`}
                >
                  <PhoneForwarded className="w-4 h-4 mr-1" />
                  Mark for Follow-Up
                </button>
              );
            }

            if (phase === 'Follow-Up') {
              return (
                <button
                  onClick={() => handlePhaseChange('Interviewing')}
                  className={`${btnBase} bg-pink-600 hover:bg-pink-700`}
                >
                  <PhoneForwarded className="w-4 h-4 mr-1" />
                  Mark for Interview
                </button>
              );
            }

            if (phase === 'Interviewing') {
              return (
                <button
                  onClick={() => {
                    if (job.joburl) window.open(job.joburl, '_blank');
                  }}
                  className={`${btnBase} bg-pink-600 hover:bg-pink-700`}
                >
                  <Search className="w-4 h-4 mr-1" />
                  Research your Interviewer
                </button>
              );
            }

            if (phase === 'Accepted') {
              return (
                <button
                  onClick={() => {
                    for (const social of SOCIAL_LINKS) {
                      window.open(social.shareUrl || social.url, '_blank');
                    }
                  }}
                  className={`${btnBase} bg-emerald-600 hover:bg-emerald-700`}
                >
                  <PartyPopper className="w-4 h-4 mr-1" />
                  Congrats! Give Snappy some props!
                </button>
              );
            }

            // Declined, Skipped, Expired, Errored, Trash
            return (
              <button
                onClick={() => handlePhaseChange('Trash')}
                className={`${btnBase} bg-red-500 hover:bg-red-600`}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Mark for Deletion
              </button>
            );
          })()}
          {job.jobphase === 'Applied' ? (
            <button
              onClick={() => handlePhaseChange('Declined')}
              className="flex-1 flex items-center justify-center p-1.5 bg-gray-400 text-white text-xs rounded hover:bg-gray-500"
              title="Mark as declined by the company"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Declined
            </button>
          ) : (
            <button
              onClick={() => handlePhaseChange('Skipped')}
              className="flex-1 flex items-center justify-center p-1.5 bg-gray-200 text-gray-600 text-xs rounded hover:bg-gray-300"
              title="Skip this job"
            >
              <SkipForward className="w-3 h-3 mr-1" />
              Skip
            </button>
          )}
          {job.jobphase === 'Trash' && (
            <button
              onClick={handleDeleteJob}
              disabled={deleting}
              className="flex-1 flex items-center justify-center p-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
              title="Permanently delete this job"
            >
              {deleting ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3 mr-1" />
              )}
              Delete
            </button>
          )}
        </div>

        {/* Resume Sections Accordion */}
        <div className="border border-gray-200 rounded-lg" data-tutorial="resume-sections">
          <button
            type="button"
            onClick={handleResumeSectionsToggle}
            aria-expanded={resumeSectionsExpanded}
            className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
          >
            <span className="text-xs font-medium text-gray-700">Resume Sections</span>
            {resumeSectionsExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>

          {resumeSectionsExpanded && (
            <div className="p-2">
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-1" role="status" aria-live="polite">
                {SUBCOMPONENTS.map((component) => {
                  const state = job[`state${component}` as keyof UserJob] as GenerationState;
                  const type = (job[`type${component}` as keyof UserJob] as GenerationType) || 'embedding';
                  const isGenerating = generatingComponents.has(component) || state === 'generating';
                  const isRetry = state === 'retry';
                  const isComplete = hasData(component) || state === 'complete';
                  const isOmit = type === 'omit';

                  // Determine background color
                  const isHighlighted = sectionContextMenu?.component === component;
                  let bgClass = 'bg-gray-100'; // not generated
                  if (isOmit) {
                    bgClass = 'bg-gray-100';
                  } else if (isGenerating || isRetry) {
                    bgClass = isRetry ? 'bg-amber-50 animate-pulse' : 'bg-purple-50 animate-pulse';
                  } else if (isComplete) {
                    bgClass = 'bg-green-50';
                  }

                  return (
                    <div
                      key={component}
                      className={`flex items-center p-1.5 rounded cursor-pointer select-none ${bgClass} ${isHighlighted ? 'ring-2 ring-primary-500 ring-inset' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setSectionContextMenu({
                          position: { x: e.clientX, y: e.clientY },
                          component,
                        });
                      }}
                    >
                      {/* Generation type indicator icon */}
                      {(type === 'embedding' || type === 'generate') && (
                        <Brain className="w-3 h-3 text-purple-500 mr-1 flex-shrink-0" />
                      )}
                      {type === 'manual' && (
                        <Settings className="w-3 h-3 text-gray-500 mr-1 flex-shrink-0" />
                      )}
                      {type === 'omit' && (
                        <XCircle className="w-3 h-3 text-red-400 mr-1 flex-shrink-0" />
                      )}
                      <span
                        className={`text-xs truncate ${isOmit ? 'text-gray-500 line-through' : 'text-gray-700'}`}
                      >
                        {SUBCOMPONENT_LABELS[component]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Files List - flat list below sections */}
      <div className="p-3" data-tutorial="files-list">
        <span className="text-xs font-medium text-gray-700 mb-2 block">Files</span>
        <div className="grid grid-cols-2 gap-1" role="status" aria-live="polite">
          {(() => {
            // Split-button styling: main area = View PDF (most common), chevron = menu.
            // Renders the same shape regardless of state (regenerating / ready / empty)
            // so the "this is a button" affordance is consistent.
            const buttonClass = (state: 'ready' | 'regenerating' | 'empty', menuOpen: boolean) => {
              const base = 'flex items-stretch rounded-md select-none overflow-hidden border transition-shadow';
              const ring = menuOpen ? ' ring-2 ring-primary-500' : '';
              if (state === 'regenerating') return `${base} bg-purple-100 border-purple-300 animate-pulse${ring}`;
              if (state === 'ready') return `${base} bg-green-100 border-green-400 shadow-sm hover:shadow${ring}`;
              return `${base} bg-gray-100 border-gray-200${ring}`;
            };
            const mainClass = (state: 'ready' | 'regenerating' | 'empty', stale?: boolean) => {
              const base = 'flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-left transition-colors';
              if (state === 'regenerating') return `${base} text-purple-900 cursor-wait`;
              if (state === 'ready') return `${base} text-green-900 hover:bg-green-200 cursor-pointer`;
              if (stale) return `${base} text-gray-500 hover:bg-gray-200 cursor-pointer`;
              return `${base} text-gray-500 cursor-default`;
            };
            const chevronClass = (state: 'ready' | 'regenerating' | 'empty') => {
              const base = 'px-1.5 flex items-center border-l transition-colors';
              if (state === 'regenerating') return `${base} border-purple-300 text-purple-700`;
              if (state === 'ready') return `${base} border-green-400 text-green-800 hover:bg-green-200 cursor-pointer`;
              return `${base} border-gray-200 text-gray-500 hover:bg-gray-200 cursor-pointer`;
            };

            const resumeStale = hasAnyResumeFile() && staleFiles.has('resume');
            const resumeState: 'ready' | 'regenerating' | 'empty' =
              regeneratingFiles.has('resume') ? 'regenerating'
              : hasAnyResumeFile() && !staleFiles.has('resume') ? 'ready'
              : 'empty';
            const coverStale = hasAnyCoverFile() && staleFiles.has('cover');
            const coverState: 'ready' | 'regenerating' | 'empty' =
              regeneratingFiles.has('cover') ? 'regenerating'
              : hasAnyCoverFile() && !staleFiles.has('cover') ? 'ready'
              : 'empty';

            return (
              <>
                {/* Resume — main click = View PDF, chevron = options menu */}
                <div className={buttonClass(resumeState, fileContextMenu?.fileType === 'resume')}>
                  <button
                    type="button"
                    className={mainClass(resumeState, resumeStale)}
                    onClick={() => {
                      if (resumeState === 'ready') {
                        const url = getFinalFileUrl('resume', 'pdf');
                        if (url) openPdf(url, 'resume');
                      } else if (resumeStale) {
                        handleRegenerateFiles('resume');
                      }
                    }}
                    disabled={resumeState === 'regenerating' || (resumeState === 'empty' && !resumeStale)}
                    title={resumeState === 'ready' ? 'View Resume PDF' : resumeStale ? 'Regenerate Resume' : 'No resume generated yet'}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">Resume</span>
                  </button>
                  <button
                    type="button"
                    className={chevronClass(resumeState)}
                    onClick={(e) => {
                      e.preventDefault();
                      setFileContextMenu({
                        position: { x: e.clientX, y: e.clientY },
                        fileType: 'resume',
                      });
                    }}
                    title="More resume options"
                    aria-label="More resume options"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Cover Letter — main click = View PDF, chevron = options menu */}
                <div className={buttonClass(coverState, fileContextMenu?.fileType === 'cover')}>
                  <button
                    type="button"
                    className={mainClass(coverState, coverStale)}
                    onClick={() => {
                      if (coverState === 'ready') {
                        const url = getFinalFileUrl('cover', 'pdf');
                        if (url) openPdf(url, 'cover');
                      } else if (coverStale) {
                        handleRegenerateFiles('cover');
                      }
                    }}
                    disabled={coverState === 'regenerating' || (coverState === 'empty' && !coverStale)}
                    title={coverState === 'ready' ? 'View Cover Letter PDF' : coverStale ? 'Regenerate Cover Letter' : 'No cover letter generated yet'}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">Cover Letter</span>
                  </button>
                  <button
                    type="button"
                    className={chevronClass(coverState)}
                    onClick={(e) => {
                      e.preventDefault();
                      setFileContextMenu({
                        position: { x: e.clientX, y: e.clientY },
                        fileType: 'cover',
                      });
                    }}
                    title="More cover letter options"
                    aria-label="More cover letter options"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            );
          })()}
        </div>
        {fileError && (
          <div className="mt-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center justify-between">
            <span>{fileError}</span>
            <button onClick={() => setFileError(null)} className="ml-2 text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}
      </div>
      </div>

      {/* Disabled-state CTA: bottom button + hover overlay */}
      {isJobDisabled && (
        <>
          <div className="absolute inset-x-0 bottom-0 p-3 bg-white border-t border-gray-100 rounded-b-lg z-10">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleGenerateAll(); }}
              disabled={generating}
              className="w-full flex items-center justify-center gap-1.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-sm font-semibold rounded-md shadow-sm disabled:opacity-50"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Generate Customized Resume!
            </button>
          </div>
          {showCardOverlay && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onUpgradeRequest?.('enable_job'); }}
              className="absolute inset-0 z-20 flex items-center justify-center bg-amber-50/80 backdrop-blur-[1px] rounded-lg border-2 border-amber-300 cursor-pointer transition-opacity"
              style={{ paddingBottom: '4rem' }}
              aria-label="Upgrade for unlimited customized resumes"
            >
              <span className="px-4 py-2 bg-white text-amber-900 text-sm font-semibold rounded-md shadow-md flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                For unlimited customized resumes, upgrade now!
              </span>
            </button>
          )}
        </>
      )}

      {/* Section Context Menu */}
      {sectionContextMenu && (
        <ContextMenu
          items={buildSectionMenuItems(sectionContextMenu.component)}
          position={sectionContextMenu.position}
          onClose={() => setSectionContextMenu(null)}
        />
      )}

      {/* File Context Menu */}
      {fileContextMenu && (
        <ContextMenu
          items={buildFileMenuItems(fileContextMenu.fileType)}
          position={fileContextMenu.position}
          onClose={() => setFileContextMenu(null)}
        />
      )}

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm px-4 py-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <span>{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="ml-2 text-gray-400 hover:text-white flex-shrink-0"
          >&times;</button>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Only re-render if the job data or handlers have changed
export const JobCard = memo(JobCardComponent, (prevProps, nextProps) => {
  // Re-render if job data changed
  if (JSON.stringify(prevProps.job) !== JSON.stringify(nextProps.job)) {
    return false;
  }
  // Re-render if selectedResume changed (affects which resume is used for generation)
  if (prevProps.selectedResume !== nextProps.selectedResume) {
    return false;
  }
  if (prevProps.autoDownloadFiles !== nextProps.autoDownloadFiles) {
    return false;
  }
  // Don't re-render if only the handler references changed (they're stable from parent)
  return true;
});
